import type { SyntheticEvent } from "react";
import { Close } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type {
  BookableObjectPermissionAssigneesDto,
  BookableObjectPermissionWindowDto
} from "../typings/resource-calendar.types";
import { formatPermissionWindowLabel } from "./format-permission-window";

type Assignees = BookableObjectPermissionAssigneesDto | undefined;

type ChipProps = {
  onOpen: () => void;
  /** True while this chip’s dialog is open. */
  expanded?: boolean;
};

export const SCHEDULE_RESOURCE_NOT_PERMITTED_DIALOG_ID = "schedule-resource-not-permitted-dialog";

/** Production marker for the not-permitted chip; list-row click guards must not use test ids. */
export const SCHEDULE_RESOURCE_NOT_PERMITTED_CHIP_ATTR = "data-schedule-not-permitted-chip";

type DialogProps = {
  open: boolean;
  onClose: () => void;
  assignees: Assignees;
  objectName: string;
};

type AssigneeNameGroupProps = {
  title: string;
  names: string[];
};

type AssigneeNameGroupsProps = {
  companies: string[];
  costCenters: string[];
  users: string[];
  companiesTitle: string;
  costCentersTitle: string;
  usersTitle: string;
};

type Translate = (key: string, options?: Record<string, string>) => string;

type GroupTitles = Omit<AssigneeNameGroupsProps, "companies" | "costCenters" | "users">;

type DeniedWindowProps = {
  window: BookableObjectPermissionWindowDto;
  language: string;
  t: Translate;
  groupTitles: GroupTitles;
};

type AssigneesBodyProps = {
  companies: string[];
  costCenters: string[];
  users: string[];
  deniedWindows: BookableObjectPermissionWindowDto[];
  language: string;
  t: Translate;
  groupTitles: GroupTitles;
};

function stopChipFromSelectingOption(event: SyntheticEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

function AssigneeNameGroup({ title, names }: Readonly<AssigneeNameGroupProps>) {
  if (names.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Typography
        variant="subtitle2"
        component="p"
        sx={{ fontWeight: 600, overflowWrap: "anywhere", textWrap: "pretty" }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, minWidth: 0, maxWidth: "100%" }}>
        {names.map(name => (
          <Chip
            key={`${title}-${name}`}
            size="small"
            variant="outlined"
            label={name}
            sx={{
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis"
              }
            }}
          />
        ))}
      </Box>
    </Stack>
  );
}

function AssigneeNameGroups({
  companies,
  costCenters,
  users,
  companiesTitle,
  costCentersTitle,
  usersTitle
}: Readonly<AssigneeNameGroupsProps>) {
  if (companies.length === 0 && costCenters.length === 0 && users.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={2}
      data-testid="schedule-resource-not-permitted-assignees"
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        px: 2,
        py: 1.5,
        borderRadius: 1.5,
        color: "text.primary",
        bgcolor: theme =>
          alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.08)
      }}
    >
      <AssigneeNameGroup title={companiesTitle} names={companies} />
      <AssigneeNameGroup title={costCentersTitle} names={costCenters} />
      <AssigneeNameGroup title={usersTitle} names={users} />
    </Stack>
  );
}

function DeniedWindowSection({ window, language, t, groupTitles }: Readonly<DeniedWindowProps>) {
  return (
    <Stack spacing={1.5} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{ fontWeight: 600, overflowWrap: "anywhere", textWrap: "pretty" }}
      >
        {formatPermissionWindowLabel(window, language, t)}
      </Typography>
      <AssigneeNameGroups
        companies={window.companies}
        costCenters={window.costCenters}
        users={window.users}
        {...groupTitles}
      />
    </Stack>
  );
}

function AssigneesDialogBody({
  companies,
  costCenters,
  users,
  deniedWindows,
  language,
  t,
  groupTitles
}: Readonly<AssigneesBodyProps>) {
  if (deniedWindows.length === 0) {
    return (
      <AssigneeNameGroups
        companies={companies}
        costCenters={costCenters}
        users={users}
        {...groupTitles}
      />
    );
  }

  return (
    <Stack spacing={3} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Stack spacing={3} divider={<Divider flexItem />} sx={{ minWidth: 0, maxWidth: "100%" }}>
        {deniedWindows.map(window => (
          <DeniedWindowSection
            key={`${window.start}-${window.end ?? "open"}`}
            window={window}
            language={language}
            t={t}
            groupTitles={groupTitles}
          />
        ))}
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 0, maxWidth: "65ch", overflowWrap: "anywhere", textWrap: "pretty" }}
      >
        {t("_scheduleResource_notPermittedDialogOtherDays")}
      </Typography>
    </Stack>
  );
}

export function ScheduleResourceNotPermittedChip({
  onOpen,
  expanded = false
}: Readonly<ChipProps>) {
  const { t } = useTranslation();

  return (
    <Chip
      size="small"
      color="warning"
      variant="filled"
      component="button"
      type="button"
      clickable
      sx={{
        cursor: "pointer",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "warning.dark",
          outlineOffset: 2
        }
      }}
      label={t("_scheduleResource_notPermitted")}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls={expanded ? SCHEDULE_RESOURCE_NOT_PERMITTED_DIALOG_ID : undefined}
      aria-label={t("_scheduleResource_notPermittedChipAria")}
      data-testid="schedule-resource-not-permitted-chip"
      {...{ [SCHEDULE_RESOURCE_NOT_PERMITTED_CHIP_ATTR]: "true" }}
      onMouseDown={stopChipFromSelectingOption}
      onClick={event => {
        stopChipFromSelectingOption(event);
        onOpen();
      }}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          stopChipFromSelectingOption(event);
          event.preventDefault();
          onOpen();
        }
      }}
    />
  );
}

export function ScheduleResourceNotPermittedDialog({
  open,
  onClose,
  assignees,
  objectName
}: Readonly<DialogProps>) {
  const { t, i18n } = useTranslation();
  const companies = assignees?.companies ?? [];
  const costCenters = assignees?.costCenters ?? [];
  const users = assignees?.users ?? [];
  const deniedWindows = (assignees?.windows ?? []).filter(
    window => window.isViewerPermitted !== true
  );
  const hasAssignees = companies.length > 0 || costCenters.length > 0 || users.length > 0;
  const groupTitles = {
    companiesTitle: t("_scheduleResource_notPermittedGroupCompanies"),
    costCentersTitle: t("_scheduleResource_notPermittedGroupCostCenters"),
    usersTitle: t("_scheduleResource_notPermittedGroupUsers")
  };
  const titleId = "schedule-resource-not-permitted-title";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      id={SCHEDULE_RESOURCE_NOT_PERMITTED_DIALOG_ID}
      aria-labelledby={titleId}
      aria-describedby={hasAssignees ? undefined : "schedule-resource-not-permitted-empty"}
      slotProps={{
        paper: {
          sx: {
            backgroundImage: "none",
            maxHeight: "90vh",
            overflowX: "hidden"
          }
        }
      }}
    >
      <DialogTitle id={titleId} sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            pr: 1,
            overflowWrap: "anywhere",
            textWrap: "balance"
          }}
        >
          {t("_scheduleResource_notPermittedDialogTitle", { name: objectName })}
        </Box>
        <IconButton
          aria-label={t("Close")}
          data-testid="schedule-resource-not-permitted-dialog-close"
          onClick={onClose}
          size="large"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          minWidth: 0,
          overflowX: "hidden",
          overflowY: "auto"
        }}
      >
        {hasAssignees ? (
          <AssigneesDialogBody
            companies={companies}
            costCenters={costCenters}
            users={users}
            deniedWindows={deniedWindows}
            language={i18n.language}
            t={t}
            groupTitles={groupTitles}
          />
        ) : (
          <DialogContentText
            id="schedule-resource-not-permitted-empty"
            sx={{
              m: 0,
              minWidth: 0,
              maxWidth: "65ch",
              overflowWrap: "anywhere",
              textWrap: "pretty"
            }}
          >
            {t("_scheduleResource_notPermittedNoAssignees")}
          </DialogContentText>
        )}
      </DialogContent>
    </Dialog>
  );
}
